import { useState } from 'react'
import './ImageUploadModal.css'

function ImageUploadModal({ isOpen, onClose, onImageSelect, productName }) {
  const [preview, setPreview] = useState(null)
  const [fileName, setFileName] = useState('')
  const [isDragging, setIsDragging] = useState(false)
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)

  const MAX_FILE_SIZE = 10 * 1024 * 1024 // 10MB
  const ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/webp']

  const validateFile = (file) => {
    setError('')

    if (!ALLOWED_TYPES.includes(file.type)) {
      setError('Please upload a JPG, PNG, or WebP image')
      return false
    }

    if (file.size > MAX_FILE_SIZE) {
      setError('File size must be less than 10MB')
      return false
    }

    return true
  }

  const handleFileSelect = (file) => {
    if (!validateFile(file)) return

    const reader = new FileReader()
    reader.onload = (e) => {
      setPreview(e.target.result)
      setFileName(file.name)
    }
    reader.readAsDataURL(file)
  }

  const handleInputChange = (e) => {
    const file = e.target.files?.[0]
    if (file) {
      handleFileSelect(file)
    }
  }

  const handleDragOver = (e) => {
    e.preventDefault()
    setIsDragging(true)
  }

  const handleDragLeave = () => {
    setIsDragging(false)
  }

  const handleDrop = (e) => {
    e.preventDefault()
    setIsDragging(false)

    const file = e.dataTransfer.files?.[0]
    if (file) {
      handleFileSelect(file)
    }
  }

  const handleUpload = async () => {
    if (!preview) {
      setError('Please select an image first')
      return
    }

    // Pass the image data back to parent (no upload, just preview)
    // The actual file will be saved to disk when order is created
    onImageSelect({
      preview: preview,
      filename: fileName,
      data: preview // Store base64 data URL for later
    })

    // Close modal
    handleClose()
  }

  const handleClose = () => {
    setPreview(null)
    setFileName('')
    setError('')
    onClose()
  }

  if (!isOpen) return null

  return (
    <div className="modal-overlay" onClick={handleClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Upload Custom Design for {productName}</h2>
          <button className="modal-close" onClick={handleClose}>✕</button>
        </div>

        <div className="modal-body">
          {!preview ? (
            <div
              className={`drag-drop-zone ${isDragging ? 'dragging' : ''}`}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
            >
              <div className="drag-drop-content">
                <div className="drag-drop-icon">📤</div>
                <h3>Drag & drop your image here</h3>
                <p>or</p>
                <label htmlFor="file-input" className="file-input-label">
                  Click to select
                </label>
                <input
                  id="file-input"
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  onChange={handleInputChange}
                  className="file-input-hidden"
                />
              </div>
              <p className="file-requirements">
                JPG, PNG, or WebP • Max 10MB
              </p>
            </div>
          ) : (
            <div className="preview-section">
              <div className="preview-image-container">
                <img src={preview} alt="Preview" className="preview-image" />
              </div>
              <div className="preview-details">
                <p className="preview-filename">
                  <strong>File:</strong> {fileName}
                </p>
                <p className="preview-info">
                  This image will be printed on your sticker
                </p>
              </div>
            </div>
          )}

          {error && <div className="error-message">{error}</div>}
        </div>

        <div className="modal-footer">
          {preview && (
            <button
              className="btn-change-image"
              onClick={() => {
                setPreview(null)
                setFileName('')
              }}
              disabled={loading}
            >
              Change Image
            </button>
          )}
          <button className="btn-cancel" onClick={handleClose} disabled={loading}>
            {preview ? 'Cancel' : 'Close'}
          </button>
          {preview && (
            <button
              className="btn-upload"
              onClick={handleUpload}
              disabled={loading}
            >
              {loading ? (
                <>
                  <span className="loading-spinner"></span>
                  Uploading...
                </>
              ) : (
                'Add to Cart'
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  )
}

export default ImageUploadModal
